/**
 * Habit Service
 *
 * Offline-first habit tracking service using IndexedDB (via Dexie.js)
 * with sync queue integration for server propagation.
 *
 * Implements streak calculation (current + longest), heatmap data generation,
 * and enforces max 30 active habits.
 *
 * @module habitService
 * @see Requirements 8.1, 8.3, 8.4, 8.5, 8.7, 12.2
 */

import type { Habit, HabitCompletion, HeatmapEntry } from '../types';
import { SyncStatus } from '../types/enums';
import { db } from './localDb';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of active (non-archived) habits a user can have. */
export const MAX_ACTIVE_HABITS = 30;

/** Maximum length for habit name. */
const MAX_NAME_LENGTH = 100;

/** Maximum length for habit description. */
const MAX_DESCRIPTION_LENGTH = 500;

// ─── Helper Functions ────────────────────────────────────────────────────────

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getTodayDate(): string {
  const now = new Date();
  return formatDate(now);
}

/**
 * Format a Date object as YYYY-MM-DD.
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD date string to a Date object (local time, midnight).
 */
function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-').map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

/**
 * Get the previous day's date string from a given YYYY-MM-DD string.
 */
function getPreviousDay(dateStr: string): string {
  const date = parseDate(dateStr);
  date.setDate(date.getDate() - 1);
  return formatDate(date);
}

/**
 * Enqueue a sync item for the habits module.
 */
async function enqueueSyncChange(
  entityId: string,
  action: 'create' | 'update' | 'delete',
  payload: Record<string, unknown>
): Promise<void> {
  const size = await db.syncQueue.count();
  if (size >= 1000) {
    // Queue is full — skip enqueue but don't prevent local write
    return;
  }

  await db.syncQueue.add({
    module: 'habits',
    entityId,
    action,
    timestamp: new Date().toISOString(),
    payload,
    retryCount: 0,
  } as unknown as import('../types').SyncQueueItem);
}

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Validate habit name (1-100 chars).
 */
function validateName(name: string): void {
  if (!name || name.length < 1 || name.length > MAX_NAME_LENGTH) {
    throw new Error(`Habit name must be between 1 and ${MAX_NAME_LENGTH} characters.`);
  }
}

/**
 * Validate habit description (0-500 chars).
 */
function validateDescription(description?: string): void {
  if (description != null && description.length > MAX_DESCRIPTION_LENGTH) {
    throw new Error(`Habit description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`);
  }
}

// ─── HabitService Interface ──────────────────────────────────────────────────

export interface HabitService {
  createHabit(habit: {
    name: string;
    description?: string;
    targetTime?: string;
    color?: string;
  }): Promise<Habit>;
  getHabits(includeArchived?: boolean): Promise<Habit[]>;
  updateHabit(habitId: string, updates: Partial<Habit>): Promise<Habit>;
  archiveHabit(habitId: string): Promise<void>;
  recordCompletion(habitId: string, date?: string): Promise<HabitCompletion>;
  removeCompletion(habitId: string, date?: string): Promise<void>;
  getCompletions(habitId: string, startDate: string, endDate: string): Promise<HabitCompletion[]>;
  calculateStreak(habitId: string): Promise<{ currentStreak: number; longestStreak: number }>;
  getHeatmapData(habitId: string, startDate: string, endDate: string): Promise<HeatmapEntry[]>;
}

// ─── HabitService Implementation ─────────────────────────────────────────────

/**
 * Creates a new habit.
 *
 * Validates input, enforces max 30 active habits, stores to IndexedDB,
 * and enqueues a sync operation.
 */
export async function createHabit(habit: {
  name: string;
  description?: string;
  targetTime?: string;
  color?: string;
}): Promise<Habit> {
  validateName(habit.name);
  validateDescription(habit.description);

  // Enforce max 30 active habits
  const allHabits = await db.habits.toArray();
  const activeCount = allHabits.filter((h) => !h.isArchived).length;

  if (activeCount >= MAX_ACTIVE_HABITS) {
    throw new Error(
      `Maximum ${MAX_ACTIVE_HABITS} active habits allowed. Archive an existing habit to create a new one.`
    );
  }

  const now = new Date().toISOString();
  const newHabit: Habit = {
    habitId: crypto.randomUUID(),
    userId: '', // Will be populated by auth context at store/API layer
    name: habit.name,
    description: habit.description ?? '',
    frequency: 'daily',
    targetTime: habit.targetTime ?? null,
    color: habit.color ?? null,
    isArchived: false,
    currentStreak: 0,
    longestStreak: 0,
    createdAt: now,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  await db.habits.put(newHabit);
  await enqueueSyncChange(newHabit.habitId, 'create', newHabit as unknown as Record<string, unknown>);

  return newHabit;
}

/**
 * Get all habits. By default returns only active habits.
 * Pass includeArchived=true to get all habits including archived ones.
 */
export async function getHabits(includeArchived = false): Promise<Habit[]> {
  const allHabits = await db.habits.toArray();
  if (includeArchived) {
    return allHabits;
  }
  return allHabits.filter((h) => !h.isArchived);
}

/**
 * Update a habit's fields.
 *
 * Validates name/description if provided, stores to IndexedDB,
 * and enqueues a sync operation.
 */
export async function updateHabit(habitId: string, updates: Partial<Habit>): Promise<Habit> {
  const existing = await db.habits.get(habitId);
  if (!existing) {
    throw new Error(`Habit not found: ${habitId}`);
  }

  if (updates.name !== undefined) {
    validateName(updates.name);
  }
  if (updates.description !== undefined) {
    validateDescription(updates.description);
  }

  const updatedHabit: Habit = {
    ...existing,
    ...updates,
    habitId: existing.habitId, // prevent overriding the ID
    updatedAt: new Date().toISOString(),
    syncStatus: SyncStatus.Pending,
  };

  await db.habits.put(updatedHabit);
  await enqueueSyncChange(habitId, 'update', updatedHabit as unknown as Record<string, unknown>);

  return updatedHabit;
}

/**
 * Archive a habit. Removes it from the active list while preserving
 * all historical completion data.
 */
export async function archiveHabit(habitId: string): Promise<void> {
  const existing = await db.habits.get(habitId);
  if (!existing) {
    throw new Error(`Habit not found: ${habitId}`);
  }

  const updatedHabit: Habit = {
    ...existing,
    isArchived: true,
    updatedAt: new Date().toISOString(),
    syncStatus: SyncStatus.Pending,
  };

  await db.habits.put(updatedHabit);
  await enqueueSyncChange(habitId, 'update', updatedHabit as unknown as Record<string, unknown>);
}

/**
 * Record a habit completion for a given date (defaults to today).
 *
 * Uses compound key [habitId+date] to prevent duplicate completions.
 * Writes to IndexedDB first, then enqueues sync.
 */
export async function recordCompletion(
  habitId: string,
  date?: string
): Promise<HabitCompletion> {
  const completionDate = date ?? getTodayDate();

  // Verify habit exists
  const habit = await db.habits.get(habitId);
  if (!habit) {
    throw new Error(`Habit not found: ${habitId}`);
  }

  // Check if already completed for this date
  const existing = await db.habitCompletions.get([habitId, completionDate]);
  if (existing) {
    return existing;
  }

  const now = new Date().toISOString();
  const completion: HabitCompletion = {
    module: 'habits',
    habitId,
    userId: habit.userId,
    date: completionDate,
    completedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  await db.habitCompletions.put(completion);
  await enqueueSyncChange(
    `${habitId}#${completionDate}`,
    'create',
    completion as unknown as Record<string, unknown>
  );

  // Recalculate and persist streak
  const streaks = await calculateStreak(habitId);
  await db.habits.update(habitId, {
    currentStreak: streaks.currentStreak,
    longestStreak: streaks.longestStreak,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  });

  return completion;
}

/**
 * Remove a habit completion for a given date (defaults to today).
 */
export async function removeCompletion(
  habitId: string,
  date?: string
): Promise<void> {
  const completionDate = date ?? getTodayDate();

  // Verify habit exists
  const habit = await db.habits.get(habitId);
  if (!habit) {
    throw new Error(`Habit not found: ${habitId}`);
  }

  await db.habitCompletions.delete([habitId, completionDate]);
  await enqueueSyncChange(
    `${habitId}#${completionDate}`,
    'delete',
    { habitId, date: completionDate }
  );

  // Recalculate and persist streak
  const now = new Date().toISOString();
  const streaks = await calculateStreak(habitId);
  await db.habits.update(habitId, {
    currentStreak: streaks.currentStreak,
    longestStreak: streaks.longestStreak,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  });
}

/**
 * Get all completions for a habit within a date range (inclusive).
 */
export async function getCompletions(
  habitId: string,
  startDate: string,
  endDate: string
): Promise<HabitCompletion[]> {
  return db.habitCompletions
    .where('[habitId+date]')
    .between([habitId, startDate], [habitId, endDate], true, true)
    .toArray();
}

/**
 * Calculate the current streak and longest streak for a habit.
 *
 * Current streak: consecutive days completed counting backwards from today
 * (or yesterday if today is not yet complete).
 *
 * Longest streak: the maximum consecutive run in the entire completion history.
 *
 * If a day is missed, the current streak resets to 0 on the following day.
 *
 * @see Requirements 8.3, 8.4, 8.5
 */
export async function calculateStreak(
  habitId: string
): Promise<{ currentStreak: number; longestStreak: number }> {
  // Get all completions sorted by date ascending
  const completions = await db.habitCompletions
    .where('habitId')
    .equals(habitId)
    .toArray();

  if (completions.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  // Build a Set of completion dates for O(1) lookup
  const completedDates = new Set(completions.map((c) => c.date));

  // Calculate current streak: go backwards from today, then yesterday
  const today = getTodayDate();
  let currentStreak = 0;

  // Check if today is completed; if so start counting from today
  // If not, start counting from yesterday
  let checkDate = completedDates.has(today) ? today : getPreviousDay(today);

  while (completedDates.has(checkDate)) {
    currentStreak++;
    checkDate = getPreviousDay(checkDate);
  }

  // Calculate longest streak: find max consecutive run in sorted dates
  const sortedDates = Array.from(completedDates).sort();
  let longestStreak = 0;
  let runLength = 1;

  for (let i = 1; i < sortedDates.length; i++) {
    const current = sortedDates[i]!;
    const expectedPrev = getPreviousDay(current);
    if (expectedPrev === sortedDates[i - 1]) {
      runLength++;
    } else {
      longestStreak = Math.max(longestStreak, runLength);
      runLength = 1;
    }
  }
  longestStreak = Math.max(longestStreak, runLength);

  return { currentStreak, longestStreak };
}

/**
 * Generate heatmap data for a habit over a date range.
 *
 * Returns an array of HeatmapEntry objects, one per day in the range,
 * marking which days have a completion record.
 *
 * @see Requirements 8.6
 */
export async function getHeatmapData(
  habitId: string,
  startDate: string,
  endDate: string
): Promise<HeatmapEntry[]> {
  // Get completions in range
  const completions = await getCompletions(habitId, startDate, endDate);
  const completedDates = new Set(completions.map((c) => c.date));

  // Generate entries for each day in range
  const entries: HeatmapEntry[] = [];
  const current = parseDate(startDate);
  const end = parseDate(endDate);

  while (current <= end) {
    const dateStr = formatDate(current);
    entries.push({
      date: dateStr,
      completed: completedDates.has(dateStr),
    });
    current.setDate(current.getDate() + 1);
  }

  return entries;
}

// ─── Default Export as Service Object ────────────────────────────────────────

/**
 * Singleton habit service instance implementing the HabitService interface.
 */
export const habitService: HabitService = {
  createHabit,
  getHabits,
  updateHabit,
  archiveHabit,
  recordCompletion,
  removeCompletion,
  getCompletions,
  calculateStreak,
  getHeatmapData,
};
